/** The rows of one run: filter by outcome, search by key or name, open a row. */

import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  Banner,
  Button,
  OutcomePill,
  SampleRows,
  SearchField,
  Select,
  SkeletonRows,
  Table,
  Td,
  Th,
  Toolbar,
} from "@/components/kit";
import { getRun } from "@/lib/integrations/api";
import { outcomeOf } from "@/lib/integrations/words";

import { useDebounced, useKeyed } from "./hooks";

const PAGE = 50;

export function RunRows({
  code,
  runId,
  counts,
  outcome: startOutcome = null,
  onOutcome,
}: {
  code: string;
  runId: string;
  counts: Record<string, number>;
  outcome?: string | null;
  onOutcome?: (outcome: string | null) => void;
}) {
  const [ownOutcome, setOwnOutcome] = useState<string | null>(startOutcome);
  const outcome = onOutcome ? startOutcome : ownOutcome;
  const setOutcome = (next: string | null) => {
    setOffset(0);
    if (onOutcome) onOutcome(next);
    else setOwnOutcome(next);
  };
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 350);
  const [offset, setOffset] = useState(0);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const page = useKeyed(`${code}|${runId}|${outcome}|${q}|${offset}`, () =>
    getRun(code, runId, { outcome, q: q || null, limit: PAGE, offset, raw: true }),
  );
  const outcomes = Object.entries(counts).filter(([key, count]) => count > 0 && outcomeOf(key).means !== "");
  const total = page.data?.total ?? 0;

  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      <Toolbar>
        <Select
          aria-label="Outcome"
          style={{ width: "auto", minWidth: "12rem" }}
          value={outcome ?? ""}
          onChange={(event) => setOutcome(event.target.value || null)}
        >
          <option value="">Every outcome</option>
          {outcomes.map(([key, count]) => (
            <option key={key} value={key}>
              {outcomeOf(key).word} ({count.toLocaleString()})
            </option>
          ))}
        </Select>
        <SearchField
          value={search}
          placeholder="Search by key or name"
          aria-label="Search rows by key or name"
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
        <span style={{ flex: 1 }} />
        <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
          {page.busy && !page.data ? "Reading…" : `${total.toLocaleString()} ${total === 1 ? "row" : "rows"}`}
        </span>
      </Toolbar>
      {page.error && (
        <Banner tone="danger" title="Could not read the rows." actions={<Button onClick={page.reload}>Try again</Button>}>
          {page.error}
        </Banner>
      )}
      {!page.data && page.busy ? (
        <SkeletonRows rows={6} columns={4} label="Reading rows" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th num>Row</Th>
              <Th>Key</Th>
              <Th>Outcome</Th>
              <Th>Why</Th>
            </tr>
          </thead>
          <tbody style={page.busy ? { opacity: 0.6 } : undefined}>
            {(page.data?.rows ?? []).length === 0 && (
              <tr>
                <Td colSpan={4} quiet>
                  No rows {outcome ? `with the outcome ${outcomeOf(outcome).word}` : ""}
                  {q ? ` matching “${q}”` : ""}.
                </Td>
              </tr>
            )}
            {(page.data?.rows ?? []).map((row) => (
              <Fragment key={row.position}>
                <tr>
                  <Td num>
                    <button
                      type="button"
                      aria-expanded={openRow === row.position}
                      aria-label={`Open row ${row.position + 1}`}
                      onClick={() => setOpenRow(openRow === row.position ? null : row.position)}
                      style={{ border: 0, background: "none", cursor: "pointer", color: "var(--accent)", display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      {openRow === row.position ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {row.position + 1}
                    </button>
                  </Td>
                  <Td style={{ fontFamily: "ui-monospace, monospace", fontSize: "var(--t-sm)", overflowWrap: "anywhere" }}>
                    {row.subjectKey ?? "—"}
                  </Td>
                  <Td>
                    <OutcomePill outcome={row.outcome} />
                  </Td>
                  <Td style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: "36rem" }}>{row.message ?? ""}</Td>
                </tr>
                {openRow === row.position && (
                  <tr>
                    <Td colSpan={4}>
                      <SampleRows theirs={row.raw} ours={row.mapped} index={row.position} total={total} />
                    </Td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>
      )}
      {total > PAGE && (
        <Toolbar>
          <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Earlier rows
          </Button>
          <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
            {(offset + 1).toLocaleString()}–{Math.min(total, offset + PAGE).toLocaleString()} of {total.toLocaleString()}
          </span>
          <Button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Later rows
          </Button>
        </Toolbar>
      )}
    </div>
  );
}
